import React, { useState, useEffect } from "react";
import { BadgeCheck, CheckCircle, CircleX, Eye, RotateCcw, Save, Trash2, Upload, X, MoreVertical } from "lucide-react";
import CustomHeading from "./common/CustomHeading";
import Button from "./common/Button";
import {
  deleteApplicantByUserId,
  recoverDeletedApplicantByUserId,
  SaveUpdateIndivudlas,
  getUserDetailsAddressUpdatedByUserId,
} from "../ActionCreator";
import Popup from "../../../../../presentationals/Popup/Popup.component";
// import { PROJECT_CONSTANTS } from "../../../../../../utils/Constant";
import { CheckFunction } from "../../../../../../layouts/MainTenantconfig";


const formatDateToInput = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  }
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  } catch (e) {
    console.error(e);
  }
  return dateStr;
};

const formatDateToApi = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    return dateStr;
  }
  return dateStr;
};

const calculateAge = (dobString) => {
  if (!dobString) return "";
  let dateObj = null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dobString)) {
    const [year, month, day] = dobString.split("-");
    dateObj = new Date(year, month - 1, day);
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(dobString)) {
    const [day, month, year] = dobString.split("-");
    dateObj = new Date(year, month - 1, day);
  } else {
    dateObj = new Date(dobString);
  }
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - dateObj.getFullYear();
  const m = today.getMonth() - dateObj.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dateObj.getDate())) {
    age--;
  }
  return age >= 0 ? age : 0;
};

const getDetailedAge = (dobString) => {
  if (!dobString) return { text: "-", isUnderage: false };
  let cleanDob = String(dobString).replace(/\//g, "-").trim();
  let birthDate = null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDob)) {
    const [year, month, day] = cleanDob.split("-").map(Number);
    birthDate = new Date(year, month - 1, day);
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(cleanDob)) {
    const [day, month, year] = cleanDob.split("-").map(Number);
    birthDate = new Date(year, month - 1, day);
  } else {
    birthDate = new Date(cleanDob);
  }
  if (!birthDate || isNaN(birthDate.getTime())) return { text: "-", isUnderage: false };
  
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();
  
  if (days < 0) {
    months--;
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }
  
  const totalYears = years + months / 12 + days / 365.25;
  const isUnderage = totalYears < 18;
  const text = `${years >= 0 ? years : 0}yr ${months >= 0 ? months : 0} months`;
  return { text, isUnderage };
};

const crossVerificationRows = [
  {
    document: "PAN Name",
    verifiedAgainst: "Aadhaar Name",
    matchRequired: "Yes",
    variance: "Minor spelling (80% match)",
    key: "cvmPanName",
  },
  {
    document: "Aadhaar DOB",
    verifiedAgainst: "PAN DOB",
    matchRequired: "Yes",
    variance: "No variance",
    key: "cvmAadhaarDob",
  },
  {
    document: "Application Address",
    verifiedAgainst: "KYC Address",
    matchRequired: "Yes",
    variance: "Same locality",
    key: "cvmAppAddress",
  },
  {
    document: "Bank Statement",
    verifiedAgainst: "KYC Records",
    matchRequired: "Prefered",
    variance: "Supportive",
    key: "cvmBankStatement",
  },
  {
    document: "Mobile No.",
    verifiedAgainst: "Bank Records",
    matchRequired: "Prefered",
    variance: "Supportive",
    key: "cvmMobileNo",
  },
];

// const transformApplicants = (data) => {
//   const roles = ["COBORROWER", "GUARANTOR", "DIRECTOR" , "PARTNER", "PROPRIETOR"];
//   let applicants = [];

//   roles.forEach((role) => {
//     if (Array.isArray(data[role])) {
//       const mapped = data[role].map((item) => ({
//         applicantType: role,
//         aadharDetails: {
//           name: item.fullName,
//           dob: "",
//           gender: "",
//           maskedAadhaarNumber: item.aadhaarNumber,
//           aadhaarVerified: item.aadhaarVerified,
//         },
//         panDetails: {
//           pan: item.panNumber,
//           panVerified: item.panVerified,
//         },
//         userId: item.userId,
//         email: item.email,
//         source: item.source,
//         deleted: item.deleted,
//       }));

//       applicants.push(...mapped);
//     }
//   });

//   return applicants;
// };

const transformApplicants = (data, savedIndividuals = []) => {
  const roles = [
    "COBORROWER",
    "GUARANTOR",
    "DIRECTOR",
    "PARTNER",
    "PROPRIETOR",
  ];


  const normalizedData = Object.keys(data).reduce((acc, key) => {
    acc[key.toLowerCase()] = data[key];
    return acc;
  }, {});

  const applicants = [];

  roles.forEach((role) => {
    const items = normalizedData[role.toLowerCase()];

    if (Array.isArray(items)) {
      const mapped = items.map((item) => {
        const saved = savedIndividuals.find((ind) => String(ind.userId) === String(item.userId));
        return {
          applicantType: role,
          aadharDetails: {
            name: item.fullName,
            dob: formatDateToInput(saved?.dob || saved?.aadharDetails?.dob || item.dob || ""),
            gender: saved?.gender || saved?.aadharDetails?.gender || item.gender || "",
            maskedAadhaarNumber: item.aadhaarNumber,
            aadhaarVerified: item.aadhaarVerified,
          },
          panDetails: {
            pan: item.panNumber || item.pannumber,
            panVerified: item.panVerified,
          },
          userId: item.userId,
          email: item.email,
          source: item.source,
          deleted: item.deleted,
        };
      });

      applicants.push(...mapped);
    }
  });

  return applicants;
};

const IndividualKYCVerification = ({
  allDetail,
  initialData,
  individualKycData,
  setLoader,
  showPopup,
  onRefresh,
  IndividualKycAPICall,
}) => {
  const [popupLocal, setPopupLocal] = useState({ show: false, popupStatus: "" });
  const [activeDropdownUserId, setActiveDropdownUserId] = useState(null);
  const [dropdownCoords, setDropdownCoords] = useState(null);

  useEffect(() => {
    const handleClose = () => {
      setActiveDropdownUserId(null);
      setDropdownCoords(null);
    };
    window.addEventListener("click", handleClose);
    window.addEventListener("scroll", handleClose, true);
    return () => {
      window.removeEventListener("click", handleClose);
      window.removeEventListener("scroll", handleClose, true);
    };
  }, []);

  const handleDropdownToggle = (e, userId) => {
    e.stopPropagation();
    if (activeDropdownUserId === userId) {
      setActiveDropdownUserId(null);
      setDropdownCoords(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom + 4,
        left: rect.right - 120,
      });
      setActiveDropdownUserId(userId);
    }
  };
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [finalData, setFinalData] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);

  // Local inputs state for current selected user
  const [addressForm, setAddressForm] = useState({
    permanentAddressLine: "",
    permanentCity: "",
    permanentState: "",
    permanentPincode: "",
    currentAddressLine: "",
    currentCity: "",
    currentState: "",
    currentPincode: "",
    currentProofType: "",
    cvmPanName: "",
    cvmAadhaarDob: "",
    cvmAppAddress: "",
    cvmBankStatement: "",
    cvmMobileNo: "",
  });

  const [nameValue, setNameValue] = useState({});
  const [panValues, setPanValues] = useState({});
  const [aadhaarValues, setAadhaarValues] = useState({});
  const [panError, setPanError] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [personForm, setPersonForm] = useState({
    applicantType: "COBORROWER",
    fullName: "",
    dob: "",
    gender: "",
    source: "",
    panNumber: "",
    aadhaarNumber: ""
  });
    const [Openbureau, setOpenbureau] = useState(false);
    const [customBureau, setcustomBureau] = useState({});
    const [popup, setshowPopup] = useState({ show: false, data: {} });

  // Sync individuals with parent-supplied initialData
  useEffect(() => {
    if (initialData) {
      setIndividuals(initialData);
    }
  }, [initialData]);

  // Sync applicants table data with API result
  useEffect(() => {
    if (individualKycData) {
      const transformed = transformApplicants(individualKycData, individuals);
      const hasDifference = transformed.length !== finalData.length || transformed.some((newItem) => {
        const existingItem = finalData.find((item) => String(item.userId) === String(newItem.userId));
        if (!existingItem) return true;

        const existingDob = existingItem.aadharDetails?.dob || "";
        const newDob = newItem.aadharDetails?.dob || "";
        const existingGender = existingItem.aadharDetails?.gender || "";
        const newGender = newItem.aadharDetails?.gender || "";
        return (
          existingDob !== newDob ||
          existingGender !== newGender ||
          existingItem.deleted !== newItem.deleted
        );
      });

      if (hasDifference || finalData.length === 0) {
        setFinalData(transformed);

        setPanValues((prev) => {
          const updated = { ...prev };
          transformed.forEach((item) => {
            if (item?.panDetails?.pan && !updated[item.userId]) {
              updated[item.userId] = item.panDetails.pan;
            }
          });
          return updated;
        });

        setAadhaarValues((prev) => {
          const updated = { ...prev };
          transformed.forEach((item) => {
            if (item?.aadharDetails?.maskedAadhaarNumber && !updated[item.userId]) {
              updated[item.userId] = item.aadharDetails.maskedAadhaarNumber;
            }
          });
          return updated;
        });
      }
    }
  }, [individualKycData, individuals, finalData.length]);

  // Sync address form when selected row/user changes
  useEffect(() => {
    if (selectedRowData?.userId) {
      const matchingIndividual = individuals.find(
        (item) => item.userId === selectedRowData.userId
      );

      if (matchingIndividual) {
        setAddressForm({
          permanentAddressLine: matchingIndividual?.address?.permanent?.addressLine || "",
          permanentCity: matchingIndividual?.address?.permanent?.city || "",
          permanentState: matchingIndividual?.address?.permanent?.state || "",
          permanentPincode: matchingIndividual?.address?.permanent?.pincode || "",
          currentAddressLine: matchingIndividual?.address?.current?.addressLine || "",
          currentCity: matchingIndividual?.address?.current?.city || "",
          currentState: matchingIndividual?.address?.current?.state || "",
          currentPincode: matchingIndividual?.address?.current?.pincode || "",
          currentProofType: matchingIndividual?.address?.current?.proofType || "",
          cvmPanName: matchingIndividual?.crossVerification?.cvmPanName || "",
          cvmAadhaarDob: matchingIndividual?.crossVerification?.cvmAadhaarDob || "",
          cvmAppAddress: matchingIndividual?.crossVerification?.cvmAppAddress || "",
          cvmBankStatement: matchingIndividual?.crossVerification?.cvmBankStatement || "",
          cvmMobileNo: matchingIndividual?.crossVerification?.cvmMobileNo || "",
        });
      } else {
        setAddressForm({
          permanentAddressLine: "",
          permanentCity: "",
          permanentState: "",
          permanentPincode: "",
          currentAddressLine: "",
          currentCity: "",
          currentState: "",
          currentPincode: "",
          currentProofType: "",
          cvmPanName: "",
          cvmAadhaarDob: "",
          cvmAppAddress: "",
          cvmBankStatement: "",
          cvmMobileNo: "",
        });
      }
    }
  }, [selectedRowData?.userId, individuals]);

  // Parse permanent address details to city, state, pincode
  useEffect(() => {
    const address = addressForm.permanentAddressLine;
    if (address) {
      const parts = address?.split(",")?.map(p => p?.trim())?.filter(Boolean);
      if (parts?.length >= 3) {
        const pincode = parts?.[parts?.length - 1];
        const state = parts?.[parts?.length - 2];
        const city = parts?.[parts?.length - 3];

        setAddressForm((prev) => {
          let updated = { ...prev };
          let changed = false;
          if (pincode && prev.permanentPincode !== pincode) {
            updated.permanentPincode = pincode;
            changed = true;
          }
          if (state && prev.permanentState !== state) {
            updated.permanentState = state;
            changed = true;
          }
          if (city && prev.permanentCity !== city) {
            updated.permanentCity = city;
            changed = true;
          }

          if (changed && selectedRowData?.userId) {
            updateIndividualsList(selectedRowData.userId, updated);
          }
          return updated;
        });
      }
    }
  }, [addressForm.permanentAddressLine]);

  // Helper to update the main individuals state array
  const updateIndividualsList = (userId, formState) => {
    setIndividuals((prevInds) => {
      const kycPayload = {
        userId,
        address: {
          permanent: {
            addressLine: formState.permanentAddressLine || "",
            city: formState.permanentCity || "",
            state: formState.permanentState || "",
            pincode: formState.permanentPincode || "",
          },
          current: {
            addressLine: formState.currentAddressLine || "",
            city: formState.currentCity || "",
            state: formState.currentState || "",
            pincode: formState.currentPincode || "",
            proofType: formState.currentProofType || "",
          },
        },
        crossVerification: {
          cvmPanName: formState.cvmPanName || "",
          cvmAadhaarDob: formState.cvmAadhaarDob || "",
          cvmAppAddress: formState.cvmAppAddress || "",
          cvmBankStatement: formState.cvmBankStatement || "",
          cvmMobileNo: formState.cvmMobileNo || "",
        },
      };

      const existingIndex = prevInds.findIndex(
        (item) => item.userId === kycPayload.userId
      );

      let updatedKycData;
      if (existingIndex > -1) {
        updatedKycData = [...prevInds];
        updatedKycData[existingIndex] = {
          ...updatedKycData[existingIndex],
          ...kycPayload,
        };
      } else {
        updatedKycData = [...prevInds, kycPayload];
      }
      return updatedKycData;
    });
  };

  const handleLocalChange = (e, field) => {
    const val = e.target.value;
    setAddressForm((prev) => {
      const updatedForm = { ...prev, [field]: val };
      if (selectedRowData?.userId) {
        updateIndividualsList(selectedRowData.userId, updatedForm);
      }
      return updatedForm;
    });
  };

  const updateAadhaarField = (userId, field, value) => {
    setFinalData((prev) =>
      prev.map((item) =>
        String(item.userId) === String(userId)
          ? {
            ...item,
            aadharDetails: { ...item.aadharDetails, [field]: value },
          }
          : item
      )
    );
  };

  const updateRowField = (userId, field, value) => {
    setFinalData((prev) =>
      prev.map((item) =>
        String(item.userId) === String(userId)
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const updateIndividualField = (userId, field, value) => {
    setIndividuals((prevInds) => {
      const existingIndex = prevInds.findIndex(
        (item) => String(item.userId) === String(userId)
      );

      let updatedIndividuals = [...prevInds];
      if (existingIndex > -1) {
        const currentInd = updatedIndividuals[existingIndex];
        updatedIndividuals[existingIndex] = {
          ...currentInd,
          [field]: value,
          // aadharDetails: {
          //   ...(currentInd.aadharDetails || {}),
          //   [field]: value,
          // },
        };
      } else {
        updatedIndividuals.push({
          userId: userId,
          [field]: value,
          aadharDetails: {
            [field]: value,
          },
        });
      }
      return updatedIndividuals;
    });
  };

  const AddressDetails = (id) => {
    setLoader(true);
    getUserDetailsAddressUpdatedByUserId(
      id,
      (callback) => {
        setLoader(false);
        const newAddress = {
          permanentAddressLine: callback?.permanentAddress ?? "",
          permanentCity: callback?.permanentCity ?? "",
          permanentState: callback?.permanentState ?? "",
          permanentPincode: callback?.permanentPincode ?? "",
          currentAddressLine: callback?.currentAddress ?? "",
          currentCity: callback?.currentCity ?? "",
          currentState: callback?.currentState ?? "",
          currentPincode: callback?.currentPincode ?? "",
        };

        setIndividuals((prevInds) => {
          const existingIndex = prevInds.findIndex((item) => item.userId === id);
          let updatedKycData = [...prevInds];
          const existingItem = existingIndex > -1 ? prevInds[existingIndex] : {};

          const mergedItem = {
            ...existingItem,
            userId: id,
            address: {
              permanent: {
                addressLine: newAddress.permanentAddressLine,
                city: newAddress.permanentCity,
                state: newAddress.permanentState,
                pincode: newAddress.permanentPincode,
              },
              current: {
                addressLine: newAddress.currentAddressLine,
                city: newAddress.currentCity,
                state: newAddress.currentState,
                pincode: newAddress.currentPincode,
                proofType: existingItem?.address?.current?.proofType || "",
              },
            },
            crossVerification: {
              cvmPanName: existingItem?.crossVerification?.cvmPanName || "",
              cvmAadhaarDob: existingItem?.crossVerification?.cvmAadhaarDob || "",
              cvmAppAddress: existingItem?.crossVerification?.cvmAppAddress || "",
              cvmBankStatement: existingItem?.crossVerification?.cvmBankStatement || "",
              cvmMobileNo: existingItem?.crossVerification?.cvmMobileNo || "",
            },
          };

          if (existingIndex > -1) {
            updatedKycData[existingIndex] = mergedItem;
          } else {
            updatedKycData.push(mergedItem);
          }

          // Update inputs state
          setAddressForm({
            permanentAddressLine: mergedItem.address.permanent.addressLine,
            permanentCity: mergedItem.address.permanent.city,
            permanentState: mergedItem.address.permanent.state,
            permanentPincode: mergedItem.address.permanent.pincode,
            currentAddressLine: mergedItem.address.current.addressLine,
            currentCity: mergedItem.address.current.city,
            currentState: mergedItem.address.current.state,
            currentPincode: mergedItem.address.current.pincode,
            currentProofType: mergedItem.address.current.proofType,
            cvmPanName: mergedItem.crossVerification.cvmPanName,
            cvmAadhaarDob: mergedItem.crossVerification.cvmAadhaarDob,
            cvmAppAddress: mergedItem.crossVerification.cvmAppAddress,
            cvmBankStatement: mergedItem.crossVerification.cvmBankStatement,
            cvmMobileNo: mergedItem.crossVerification.cvmMobileNo,
          });

          return updatedKycData;
        });
      },
      (error) => {
        setLoader(false);
        console.error("Error fetching address details:", error);
      }
    );
  };

  const handleSelectedRow = (value, id) => {
    const valss = selectedRow === id ? null : id;
    setSelectedRow(valss);
    if (valss !== null && valss !== undefined && valss !== "") {
      setSelectedRowData(value ?? "");
      AddressDetails(value.userId);
    } else {
      setSelectedRowData(null);
    }
  };

  const handlechecked = (e) => {
    const isChecked = e.target.checked;
    setSameAsPermanent(isChecked);

    setAddressForm((prev) => {
      let updatedForm;
      if (isChecked) {
        updatedForm = {
          ...prev,
          currentAddressLine: prev.permanentAddressLine || "",
          currentCity: prev.permanentCity || "",
          currentState: prev.permanentState || "",
          currentPincode: prev.permanentPincode || "",
          currentProofType: prev.permanentProofType || "",
        };
      } else {
        updatedForm = {
          ...prev,
          currentAddressLine: "",
          currentCity: "",
          currentState: "",
          currentPincode: "",
          currentProofType: "",
        };
      }

      if (selectedRowData?.userId) {
        updateIndividualsList(selectedRowData.userId, updatedForm);
      }
      return updatedForm;
    });
  };



  const handleDeleteUser = (row) => {
    deleteApplicantByUserId(
      row.userId,
      (response) => {
        if (response?.status === 200 || response?.message === "success") {
          showPopup("Applicant deleted successfully");
          if (onRefresh) onRefresh();
          if (IndividualKycAPICall) IndividualKycAPICall();
        } else {
          showPopup(response?.data?.message || "Failed to delete applicant");
        }
      },
      (error) => {
        console.error("Error deleting applicant:", error);
      }
    );
  };

  const handleRecoverUser = (row) => {
    recoverDeletedApplicantByUserId(
      row.userId,
      (response) => {
        if (response?.status === 200 || response?.message === "success" || response?.success === true) {
          showPopup("Applicant recovered successfully");
          if (onRefresh) onRefresh();
          if (IndividualKycAPICall) IndividualKycAPICall();
        } else {
          showPopup(response?.data?.message || response?.message || "Failed to recover applicant");
        }
      },
      (error) => {
        console.error("Error recovering applicant:", error);
      }
    );
  };

  const handleAddPersonSubmit = (e) => {
    e.preventDefault();
    setBtnLoading(true);
    const newIndividual = {
      address: {
        permanent: {
          addressLine: "",
          city: "",
          state: "",
          pincode: "",
        },
        current: {
          addressLine: "",
          city: "",
          state: "",
          pincode: "",
          proofType: "",
        },
      },
      crossVerification: {
        cvmPanName: "",
        cvmAadhaarDob: "",
        cvmAppAddress: "",
        cvmBankStatement: "",
        cvmMobileNo: "",
      },
      userId: "",
      borrowerUserId: allDetail?.userId,
      fullName: personForm.fullName || "",
      dob: formatDateToApi(personForm.dob),
      gender: personForm.gender,
      applicantType: personForm.applicantType,
      panNumber: personForm.panNumber,
      aadhaarNumber: personForm.aadhaarNumber,
    };
    SaveUpdateIndivudlas(
      { individuals: [newIndividual] },
      (res) => {
        setBtnLoading(false);
        if (res?.status === 200 || res?.success === true) {
          showPopup("Person added successfully");
          setIsModalOpen(false);
          setPersonForm({
            applicantType: "COBORROWER",
            fullName: "",
            dob: "",
            gender: "",
            source: "",
            panNumber: "",
            aadhaarNumber: "",
          });
          if (onRefresh) onRefresh();
          if (IndividualKycAPICall) IndividualKycAPICall();
        } else {
          showPopup(res?.message || "Error adding person");
        }
      },
      (err) => {
        setBtnLoading(false);
        showPopup("Error adding person");
        console.error("Error adding person:", err);
      }
    );
  };

  const handleSave = () => {
    setBtnLoading(true);
    const sanitizedIndividuals = finalData.map((row) => {
      const existingIndividual = individuals.find((ind) => String(ind.userId) === String(row.userId)) || {};
      const { _id, ...rest } = existingIndividual;

      const panNumber = row?.panDetails?.pan || panValues[row.userId] || "";
      const aadhaarNumber = row?.aadharDetails?.maskedAadhaarNumber || aadhaarValues[row.userId] || "";
      const dob = row?.aadharDetails?.dob || rest.dob || "";
      const gender = row?.aadharDetails?.gender || rest.gender || "";

      return {
        ...rest,
        userId: row.userId || "",
        borrowerUserId: allDetail?.userId,
        fullName: nameValue[row.userId] !== undefined ? nameValue[row.userId] : (row?.aadharDetails?.name || rest.fullName || ""),
        dob: formatDateToApi(dob),
        gender,
        panNumber,
        aadhaarNumber,
        applicantType: row.applicantType || rest.applicantType || "COBORROWER",
        address: rest.address || {
          permanent: {
            addressLine: "",
            city: "",
            state: "",
            pincode: "",
          },
          current: {
            addressLine: "",
            city: "",
            state: "",
            pincode: "",
            proofType: "",
          },
        },
        crossVerification: rest.crossVerification || {
          cvmPanName: "",
          cvmAadhaarDob: "",
          cvmAppAddress: "",
          cvmBankStatement: "",
          cvmMobileNo: "",
        },
      };
    });
    SaveUpdateIndivudlas(
      { individuals: sanitizedIndividuals },
      (res) => {
        setBtnLoading(false);
        if (res?.status === 200 || res?.success === true) {
          showPopup("Individual KYC saved successfully");
          if (onRefresh) onRefresh();
          if (IndividualKycAPICall) IndividualKycAPICall();
        } else {
          showPopup(res?.message || "Error saving individual KYC");
        }
      },
      (err) => {
        setBtnLoading(false);
        showPopup("Error saving individual KYC");
        console.error("Error saving individual KYC", err);
      }
    );
  };

    const handlePopup = (value, data) => {
    setshowPopup({ show: value, data: data });
  };



  return (
    <div style={{ marginTop: "12px" }}>
      {popupLocal.show && (
        <Popup popupStatus={popupLocal.popupStatus} closePopup={() => setPopupLocal({ show: false, popupStatus: "" })} />
      )}

      <CustomHeading
        title="Details of Individuals"
        subtitle="Verify identity documents for all applicants, co-applicants, and guarantors."
        buttonText={individualKycData?.status}
        icon={individualKycData?.status === "Negative" ? CircleX : CheckCircle}
        buttonColor={individualKycData?.status === "Negative" ? "#EB6062" : "#4CAF50"}
        borderColor={individualKycData?.status === "Negative" ? "#7B3D15" : "#8A38F5"}
        background={individualKycData?.status === "Negative" ? "#B752132B" : "#FAF5FE"}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Button
          title="Save Data"
          onClick={handleSave}
          btnLoading={btnLoading}
        />
      </div>

      {isModalOpen && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px" }}>
              <h5 style={{ ...sectionTitle, color: "#024e5a", margin: 0 }}>Add New Person</h5>
              <X style={{ cursor: "pointer", color: "#64748b", width: 'auto' }} size={20} onClick={() => setIsModalOpen(false)} />
            </div>
            <form onSubmit={handleAddPersonSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={label}>Applicant Type</label>
                  <select
                    style={selectInput}
                    value={personForm.applicantType}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, applicantType: e.target.value }))}
                    required
                  >
                    <option value="COBORROWER">COBORROWER</option>
                    <option value="Director">Director</option>
                    <option value="Partner">Partner</option>
                    <option value="Proprietor">Proprietor</option>
                  </select>
                </div>
                <div>
                  <label style={label}>Full Name</label>
                  <input
                    type="text"
                    style={inputFull}
                    value={personForm.fullName}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, fullName: e.target.value }))}
                    placeholder="Enter Full Name"
                    required
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={label}>Date of Birth</label>
                  <input
                    type="date"
                    style={inputFull}
                    value={personForm.dob}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, dob: e.target.value }))}
                    required
                  />
                </div>
                 <div>
                  <label style={label}>Age</label>
                  <div style={{ ...inputFull, background: "#f1f5f9", display: "flex", alignItems: "center", minHeight: "38px" }}>
                    <span style={{ color: getDetailedAge(personForm.dob).isUnderage ? "#dc2626" : "#0f172a", fontWeight: getDetailedAge(personForm.dob).isUnderage ? "bold" : "normal" }}>
                      {getDetailedAge(personForm.dob).text}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={label}>Gender</label>
                  <select
                    style={selectInput}
                    value={personForm.gender}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, gender: e.target.value }))}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={label}>Source</label>
                  <input
                    type="text"
                    style={inputFull}
                    value={personForm.source}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, source: e.target.value }))}
                    placeholder="e.g. Reference"
                    required
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={label}>PAN Number</label>
                  <input
                    type="text"
                    maxLength={10}
                    style={inputFull}
                    value={personForm.panNumber}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, panNumber: e.target.value.toUpperCase() }))}
                    placeholder="ABCDE1234F"
                    required
                  />
                </div>
                <div>
                  <label style={label}>Aadhaar Number</label>
                  <input
                    type="text"
                    maxLength={12}
                    style={inputFull}
                    value={personForm.aadhaarNumber}
                    onChange={(e) => setPersonForm(prev => ({ ...prev, aadhaarNumber: e.target.value }))}
                    placeholder="12 Digit Aadhaar"
                    required
                  />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "end", gap: "8px", borderTop: "1px solid #e2e8f0", paddingTop: "12px", marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    backgroundColor: "#f1f5f9",
                    color: "#334155",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    backgroundColor: "#024e5a",
                    color: "#fff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ fontFamily: "'Segoe UI', sans-serif", background: "#F9FAFB" }}>
        <style>{`
          .wogom-action-dots-btn:hover {
            background-color: #f1f5f9 !important;
          }
          .wogom-dropdown-item:hover {
            background-color: #f8fafc !important;
            color: #0f172a !important;
          }
          .wogom-dropdown-item-delete:hover {
            background-color: #fef2f2 !important;
            color: #dc2626 !important;
          }
        `}</style>
        {/* ── Personal Details Card ── */}
        <div style={{ ...card }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ ...sectionTitle, color: "#934AF2" }}>
              Personal Details - Applicant
            </h5>
            <button
              onClick={() => setIsModalOpen(true)}
              style={{
                height: "fit-content",
                margin: '0',
                backgroundColor: "#024e5a",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600
              }}
            >
              Add Person
            </button>
          </div>
          <div style={{ overflowX: "auto", marginTop: "12px" }}>
            <table style={tableBase}>
              <thead>
                <tr>
                  {[
                    "Info",
                    "Applicant Type",
                    "Full Name",
                    "Date of Birth",
                    "Age",
                    "Gender",
                    "Source",
                    "PAN Number",
                    // "PAN Verified",
                    "Aadhar Number",
                    // "Action",
                  ].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {finalData && finalData?.length > 0 ? (
                  finalData
                    ?.filter((item) => item.deleted !== true)
                    .map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #F0F0F0",
                          background: selectedRow === i + 1 ? "#f9eee2" : "",
                        }}
                      >
                        <td
                          style={{
                            ...td,
                            textAlign: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => handleSelectedRow(row, i + 1)}
                        >
                          <i className="fa fa-info"></i>
                        </td>

                        <td style={td}>{row?.applicantType ?? ""}</td>

                        <td style={td}>
                          <input
                            style={{
                              border: "1px solid rgb(217, 217, 217)",
                              borderRadius: "6px",
                              padding: "8px 10px",
                            }}
                            value={
                              nameValue[row.userId] !== undefined
                                ? nameValue[row.userId]
                                : row?.aadharDetails?.name || ""
                            }
                            placeholder="Enter Full Name"
                            onChange={(e) =>
                              setNameValue((prev) => ({
                                ...prev,
                                [row.userId]: e?.target?.value,
                              }))
                            }
                          />
                        </td>

                        <td style={td}>
                          <input
                            type="date"
                            style={{
                              border: "1px solid rgb(217, 217, 217)",
                              borderRadius: "6px",
                              padding: "8px 10px",
                            }}
                            value={row?.aadharDetails?.dob ?? ""}
                            onChange={(e) => {
                              updateAadhaarField(row.userId, "dob", e.target.value);
                              updateIndividualField(row.userId, "dob", e.target.value);
                            }}
                          />
                        </td>
                         <td style={td}>
                          <span style={{ color: getDetailedAge(row?.aadharDetails?.dob).isUnderage ? "#dc2626" : "#0f172a", fontWeight: getDetailedAge(row?.aadharDetails?.dob).isUnderage ? "bold" : "normal" }}>
                            {getDetailedAge(row?.aadharDetails?.dob).text}
                          </span>
                        </td>


                        <td style={td}>
                          <select
                            style={{
                              border: "1px solid rgb(217, 217, 217)",
                              borderRadius: "6px",
                              padding: "8px 10px",
                            }}
                            value={
                              row?.aadharDetails?.gender
                                ? (row.aadharDetails.gender.toLowerCase() === "other" ? "Other" : row.aadharDetails.gender)
                                : ""
                            }
                            onChange={(e) => {
                              updateAadhaarField(row.userId, "gender", e.target.value);
                              updateIndividualField(row.userId, "gender", e.target.value);
                            }}
                          >
                            <option value="">Select Value</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </td>
                        <td>{row?.source}</td>
                        <td style={td}>
                          {row?.panDetails?.panVerified ? (
                            <span>{row?.panDetails?.pan}</span>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <input
                                type="text"
                                placeholder="ABCDE1234F"
                                value={panValues[row.userId] || ""}
                                maxLength={10}
                                onChange={(e) =>
                                  setPanValues((prev) => ({
                                    ...prev,
                                    [row.userId]:
                                      e.target.value.toUpperCase(),
                                  }))
                                }
                                style={{
                                  border: "1px solid rgb(217, 217, 217)",
                                  borderRadius: "6px",
                                  padding: "8px 10px",
                                }}
                              />
                            </div>
                          )}
                        </td>

                        

                        <td style={td}>
                          <input
                            type="text"
                            placeholder="Enter Aadhaar Number"
                            value={
                              aadhaarValues[row.userId] !== undefined
                                ? aadhaarValues[row.userId]
                                : row?.aadharDetails?.maskedAadhaarNumber || ""
                            }
                            maxLength={12}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAadhaarValues((prev) => ({
                                ...prev,
                                [row.userId]: val,
                              }));
                              updateAadhaarField(row.userId, "maskedAadhaarNumber", val);
                              updateIndividualField(row.userId, "aadhaarNumber", val);
                            }}
                            style={{
                              border: "1px solid rgb(217, 217, 217)",
                              borderRadius: "6px",
                              padding: "8px 10px",
                            }}
                          />
                        </td>

                        {/* <td style={{ ...td, position: "relative" }}>
                          <button
                            onClick={(e) => handleDropdownToggle(e, row.userId)}
                            style={{
                              backgroundColor: "transparent",
                              border: "none",
                              cursor: "pointer",
                              padding: "8px",
                              borderRadius: "50%",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "background-color 0.15s",
                              outline: "none",
                            }}
                            className="wogom-action-dots-btn"
                          >
                            <MoreVertical size={18} color="#64748b" style={{ width: "auto" }} />
                          </button>
                        </td> */}
                      </tr>
                    ))
                    .reverse()
                ) : ""}
              </tbody>
            </table>
          </div>

          {finalData &&
            finalData?.length > 0 &&
            finalData?.filter((item) => item.deleted === true).length > 0 && (
              <>
                <h5 style={{ ...sectionTitle, color: "#934AF2", marginTop: "24px" }}>
                  Deleted Applicant
                </h5>
                <div style={{ overflowX: "auto", marginTop: "12px" }}>
                  <table style={tableBase}>
                    <thead>
                      <tr>
                        {[
                          "Info",
                          "Applicant Type",
                          "Full Name",
                          "Date of Birth",
                          "Age",
                          "Shareholding(%)",
                          "Gender",
                          "Source",
                          "PAN Number",
                          // "PAN Verified",
                          "Aadhar Number",
                          "Action",
                        ].map((h) => (
                          <th key={h} style={th}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {finalData
                        ?.filter((item) => item?.deleted === true)
                        ?.map((row, i) => (
                          <tr
                            key={`deleted-${i}`}
                            style={{ background: "#f5f5f5" }}
                          >
                            <td
                              style={{
                                ...td,
                                textAlign: "center",
                                cursor: "pointer",
                              }}
                              onClick={() => handleSelectedRow(row, i + 1)}
                            >
                              <i className="fa fa-info"></i>
                            </td>

                            <td style={td}>{row?.applicantType || "-"}</td>

                            <td style={td}> {row?.aadharDetails?.name || "-"}</td>

                            <td style={td}>{row?.aadharDetails?.dob || "-"}</td>

                             <td style={td}>
                              <span style={{ color: getDetailedAge(row?.aadharDetails?.dob).isUnderage ? "#dc2626" : "#0f172a", fontWeight: getDetailedAge(row?.aadharDetails?.dob).isUnderage ? "bold" : "normal" }}>
                                {getDetailedAge(row?.aadharDetails?.dob).text}
                              </span>
                            </td>

                             <td style={td}>{(row?.shareholding !== undefined && row?.shareholding !== null && row?.shareholding !== "") ? row.shareholding : "-"}</td>

                            <td style={td}>{row?.aadharDetails?.gender || "-"}</td>

                            <td style={td}>{row?.source || "-"}</td>

                            <td style={td}>{row?.panDetails?.pan || "-"}</td>

                            {/* <td style={td}>
                              {row?.panDetails?.panVerified
                                ? "Verified"
                                : "Not Verified"}
                            </td> */}

                            <td style={td}>
                              {row?.aadharDetails?.maskedAadhaarNumber || "-"}
                            </td>



                            <td style={td}>
                              <button
                                className="btn btn-primary"
                                style={{
                                  margin: "0",
                                  backgroundColor: "#43a047",
                                  borderColor: "#43a047",
                                  height: "36px",
                                }}
                                onClick={() => handleRecoverUser(row)}
                              >
                                <RotateCcw
                                  height={20}
                                  style={{
                                    marginTop: "-3px",
                                    width: "auto",
                                  }}
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </div>

        {selectedRow !== "" &&
          selectedRow !== null &&
          selectedRow !== undefined ? (
          <>
            {/* ── Address Verification Card ── */}
            <div style={{ ...card }}>
              <h5 style={{ ...sectionTitle, color: "#934AF2" }}>
                Address Verification{" "}
                {`(${selectedRowData?.aadharDetails?.name})`}
              </h5>

              {/* Permanent Address */}
              <div style={{ marginTop: "28px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <p style={{ ...subHeading, margin: 0 }}>
                    Permanent Address (As per Aadhaar)
                  </p>
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label style={label}>Address Line</label>
                  <input
                    type="text"
                    placeholder=""
                    value={addressForm.permanentAddressLine}
                    onChange={(e) => handleLocalChange(e, "permanentAddressLine")}
                    style={inputFull}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  {[
                    { field: "permanentCity", label: "City" },
                    { field: "permanentState", label: "State" },
                    { field: "permanentPincode", label: "Pincode" },
                  ].map(({ field, label: lbl }) => (
                    <div key={field}>
                      <label style={label}>{lbl}</label>
                      <input
                        type="text"
                        placeholder="-"
                        value={addressForm[field]}
                        onChange={(e) => handleLocalChange(e, field)}
                        style={inputFull}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Current Address */}
              <div style={{ marginTop: "18px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <p style={{ ...subHeading, margin: 0 }}>Current Address</p>
                  <span style={{ fontSize: "11px", color: "#e53935" }}>
                    *Mandatory if different from Permanent
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={sameAsPermanent}
                    id="checks"
                    onChange={(e) => handlechecked(e)}
                  />
                  <label htmlFor="checks" style={{ ...subHeading, margin: 0 }}>
                    Same as Permanent Address
                  </label>
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label style={label}>Address Line</label>
                  <input
                    type="text"
                    placeholder=""
                    value={addressForm.currentAddressLine}
                    onChange={(e) => handleLocalChange(e, "currentAddressLine")}
                    style={inputFull}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  {[
                    { field: "currentCity", label: "City" },
                    { field: "currentState", label: "State" },
                    { field: "currentPincode", label: "Pincode" },
                  ].map(({ field, label: lbl }) => (
                    <div key={field}>
                      <label style={label}>{lbl}</label>
                      <input
                        type="text"
                        placeholder="-"
                        value={addressForm[field]}
                        onChange={(e) => handleLocalChange(e, field)}
                        style={inputFull}
                      />
                    </div>
                  ))}
                </div>

                
              </div>
            </div>

            {/* ── Cross Verification Matrix ── */}
            <div style={{ ...card }}>
              <h5 style={{ ...sectionTitle, color: "#934AF2" }}>
                Cross Verification Matrix{" "}
                {`(${selectedRowData?.aadharDetails?.name})`}
              </h5>
              <div
                style={{
                  marginTop: "14px",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px solid #b2cdc8",
                }}
              >
                <table style={tableBase}>
                  <thead>
                    <tr
                      style={{
                        background:
                          "linear-gradient(90deg, #a8c8c0 0%, #c0d8d2 100%)",
                      }}
                    >
                      {[
                        "Document",
                        "Verified Against",
                        "Match Required",
                        "Acceptable Variance",
                        "Status",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            ...th,
                            background: "transparent",
                            color: "#2d4a45",
                            fontWeight: "500",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {crossVerificationRows.map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          background: i % 2 === 0 ? "#ffffff" : "#f5faf9",
                          borderBottom: "1px solid #e4eeec",
                        }}
                      >
                        <td style={td}>{row.document}</td>
                        <td style={td}>{row.verifiedAgainst}</td>
                        <td style={td}>{row.matchRequired}</td>
                        <td style={td}>{row.variance}</td>
                        <td style={td}>
                          <select
                            value={addressForm[row.key] || ""}
                            onChange={(e) => handleLocalChange(e, row.key)}
                            style={{
                              border: "1px solid #D9D9D9",
                              borderRadius: "6px",
                              padding: "5px 10px",
                              fontSize: "12px",
                              color: "#3a3a3a",
                              background: "#fff",
                              cursor: "pointer",
                              minWidth: "100px",
                              outline: "none",
                            }}
                          >
                            <option value="">Status</option>
                            <option value="verified">Verified</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          ""
        )}
      </div>

      

      

      {activeDropdownUserId && dropdownCoords && (
        <div
          style={{
            position: "fixed",
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
            zIndex: 99999,
            minWidth: "120px",
            padding: "4px 0",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          
         
         
          <button
            onClick={() => {
              const row = finalData.find((r) => String(r.userId) === String(activeDropdownUserId));
              setActiveDropdownUserId(null);
              setDropdownCoords(null);
              if (row) handleDeleteUser(row);
            }}
            style={{ ...dropdownItemStyle, color: "#ef4444" }}
            className="wogom-dropdown-item-delete"
          >
            <Trash2 size={14} style={{ marginRight: "8px", width: "auto" }} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ── Shared styles ──────────────────────────────────────────
const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(15, 23, 42, 0.4)",
  backdropFilter: "blur(4px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};
const modalBox = {
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "24px",
  width: "90%",
  maxWidth: "500px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  border: "1px solid #cbd5e1",
  boxSizing: "border-box",
};
const card = {
  background: "rgba(255, 255, 255, 0.95)",
  border: "1px solid #cbd5e1",
  borderRadius: "16px",
  padding: "20px",
  marginTop: "16px",
  boxShadow:
    "0 4px 20px -2px rgba(15, 23, 42, 0.04), 0 2px 8px -1px rgba(15, 23, 42, 0.02)",
};
const sectionTitle = {
  fontSize: "16px",
  fontWeight: "600",
  margin: "0 0 6px 0",
};
const subHeading = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#334155",
  margin: "0 0 10px 0",
};
const label = {
  fontSize: "12px",
  fontWeight: "500",
  color: "#475569",
  display: "block",
  marginBottom: "5px",
};
const inputFull = {
  width: "100%",
  border: "1.5px solid #cbd5e1",
  borderRadius: "10px",
  padding: "8px 12px",
  fontSize: "13px",
  color: "#0f172a",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
  transition: "all 0.15s ease",
};
const selectInput = { ...inputFull, cursor: "pointer" };
const tableBase = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "0 4px",
  fontSize: "13px",
};
const th = {
  padding: "11px 14px",
  textAlign: "left",
  fontWeight: "600",
  color: "#334155",
  fontSize: "12px",
  background: "#f1f5f9",
  borderBottom: "1.5px solid #cbd5e1",
};
const td = { padding: "10px 14px", color: "#0f172a", fontSize: "12px" };
const dropdownItemStyle = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "8px 12px",
  backgroundColor: "transparent",
  border: "none",
  textAlign: "left",
  fontSize: "12.5px",
  fontWeight: "650",
  color: "#334155",
  cursor: "pointer",
  outline: "none",
  transition: "background-color 0.1s",
  fontFamily: "inherit",
};

export default IndividualKYCVerification;
